// src/lib/industry.ts

// Canonical industry keys we persist on Company.industry
export type IndustryKey =
  | "fnb"
  | "retail"
  | "services"
  | "professional"
  | "online_digital"
  | "other";

// Small UI nudges per industry
export const INDUSTRY_MICRO: Record<
  IndustryKey,
  { defaultPay?: string; hint?: string; placeholder?: string }
> = {
  fnb: {
    defaultPay: "Card",
    hint: "Add tips/service charge if needed.",
    placeholder: "e.g., Latte, Sandwich, Set lunch",
  },
  retail: {
    defaultPay: "Cash",
    hint: "Scan barcodes or type SKU to speed checkout.",
    placeholder: "Scan or type SKU / item name",
  },
  services: {
    defaultPay: "On Account",
    hint: "Add appointment/job reference to invoices.",
    placeholder: "Describe service (e.g., Haircut, AC repair)",
  },
  professional: {
    defaultPay: "On Account",
    hint: "Add project or client to invoices; use retainers.",
    placeholder: "Describe service (e.g., Logo design, Tax prep)",
  },
  online_digital: {
    defaultPay: "Card",
    hint: "Use checkout links for digital goods, courses, or subscriptions.",
    placeholder: "e.g., Notion template, Video course",
  },
  other: {},
};

// Normalize free-form text (wizard entry or legacy values) into canonical keys
export function normalizeIndustry(s?: string | null): IndustryKey {
  const v = (s || "").trim().toLowerCase();
  if (!v) return "other";
  if (
    ["f&b", "fnb", "cafe", "café", "coffee", "restaurant", "food", "dining"].some(
      (k) => v.includes(k)
    )
  )
    return "fnb";
  if (["retail", "shop", "store", "boutique", "kiosk", "convenience"].some((k) => v.includes(k)))
    return "retail";
  if (
    [
      "salon",
      "barber",
      "spa",
      "wellness",
      "repair",
      "cleaning",
      "plumber",
      "electrician",
      "landscap", // matches landscape/landscaping
    ].some((k) => v.includes(k))
  )
    return "services";
  if (
    [
      "freelance",
      "consult",
      "agency",
      "design",
      "developer",
      "accountant",
      "coach",
      "tutor",
      "photo",
      "video",
    ].some((k) => v.includes(k))
  )
    return "professional";
  if (
    [
      "creator",
      "content",
      "newsletter",
      "course",
      "digital",
      "download",
      "template",
      "gumroad",
      "shopify",
      "etsy",
      "saas",
      "subscription",
      "notion",
    ].some((k) => v.includes(k))
  )
    return "online_digital";
  return "other";
}

// Primary app sections (used by sidebar, dashboard tiles, etc.)
export const BASE_SECTIONS = ["Sales", "Invoices", "Products", "Projects", "Reports"] as const;
export type SectionKey = (typeof BASE_SECTIONS)[number];

// Preferred ordering of sections per industry (UI-only; routes/permissions unchanged)
export const ORDER_BY_INDUSTRY: Record<IndustryKey, SectionKey[]> = {
  fnb: ["Sales", "Products", "Invoices", "Reports"],
  retail: ["Sales", "Products", "Invoices", "Reports"],
  services: ["Invoices", "Sales", "Projects", "Reports"],
  professional: ["Invoices", "Sales", "Projects", "Reports"],
  online_digital: ["Sales", "Invoices", "Reports", "Products"],
  other: ["Sales", "Invoices", "Products", "Reports"],
};

// Helper to produce a safe ordered list of sections
export function orderSections(industry: string | null | undefined): SectionKey[] {
  const key = normalizeIndustry(industry);
  const pref = ORDER_BY_INDUSTRY[key] ?? BASE_SECTIONS;
  // Keep only known sections, then append any missing from base
  return pref
    .filter((s): s is SectionKey => (BASE_SECTIONS as readonly string[]).includes(s))
    .concat(BASE_SECTIONS.filter((s) => !pref.includes(s)));
}

// Optional helper: resolve "Card" to an actual payment method label in the account
export function resolveDefaultPayName(
  wanted: string | undefined,
  methods: { name: string; type?: string }[] = []
): string | undefined {
  if (!wanted) return wanted;
  if (wanted.toLowerCase() !== "card") return wanted;

  // Prefer explicit Visa/MasterCard naming if present
  const exact = methods.find((m) => m.name.toLowerCase() === "visa/mastercard");
  if (exact) return exact.name;

  // Or any method typed as 'card'
  const typed = methods.find((m) => (m.type || "").toLowerCase() === "card");
  if (typed) return typed.name;

  return wanted; // fallback to the generic label
}

// Convenience to fetch the micro-config for a company/industry string
export function microFor(industry?: string | null) {
  return INDUSTRY_MICRO[normalizeIndustry(industry)];
}
